'use client'

import React, { useEffect, useState } from 'react'
import { IconCurrencyDollar } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { EMPTY_WIDGET_SURFACE_CLASS } from './widget-surface'

interface Quote {
  symbol: string
  shortName?: string
  price: number
  change: number
  changePercent: number
}

interface FinanceWidgetProps {
  ticker: string
  className?: string
  onClick?: () => void
}

export function FinanceWidget({ ticker, className, onClick }: FinanceWidgetProps) {
  const [quote, setQuote] = useState<Quote | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!ticker?.trim()) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(false)
    fetch(`/api/finance/quote?ticker=${encodeURIComponent(ticker.trim())}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed'))))
      .then((data) => {
        setQuote(data)
        setError(false)
      })
      .catch(() => {
        setQuote(null)
        setError(true)
      })
      .finally(() => setLoading(false))
  }, [ticker])

  const hasQuote = quote != null && !error
  const isPositive = hasQuote && quote.change >= 0

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(EMPTY_WIDGET_SURFACE_CLASS, 'flex-col items-start justify-center', className)}
      aria-label={ticker ? `Stock price for ${ticker}` : 'Finance'}
    >
      <div className="mb-2 flex items-center gap-1.5 text-muted-foreground">
        <IconCurrencyDollar size={20} />
        <span className="text-xs font-medium">{ticker || 'Finance'}</span>
      </div>
      {loading && (
        <div className="space-y-2">
          <div className="h-7 w-28 animate-pulse rounded-lg bg-muted/50" />
          <div className="h-4 w-20 animate-pulse rounded-full bg-muted/40" />
        </div>
      )}
      {error && !loading && <p className="text-sm text-muted-foreground">Unable to load quote</p>}
      {hasQuote && !loading && (
        <div className="flex flex-1 flex-col justify-center">
          <div className="text-2xl font-semibold tabular-nums text-foreground">
            ${typeof quote.price === 'number' ? quote.price.toFixed(2) : quote.price}
          </div>
          <div
            className={cn(
              'mt-1 text-sm tabular-nums',
              isPositive
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-600 dark:text-red-400'
            )}
          >
            {isPositive ? '+' : ''}
            {quote.change.toFixed(2)} ({isPositive ? '+' : ''}
            {quote.changePercent.toFixed(2)}%)
          </div>
        </div>
      )}
    </button>
  )
}
